import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { observer } from "mobx-react";
import { ClientType } from "models/ClientBrain/OrganizationContext";
import React from "react";
import ClientBrainStore from "stores/ClientBrainStore";
import ClientBrainSectionCard from "../ClientBrainSectionCard";

interface ClientTypeOption {
    value: ClientType;
    label: string;
}

const CLIENT_TYPE_OPTIONS: ClientTypeOption[] = [
    { value: ClientType.Pharma, label: "Pharma" },
    { value: ClientType.Payer, label: "Payer" },
    { value: ClientType.Provider, label: "Provider" },
    { value: ClientType.Other, label: "Other" },
];

const CLIENT_TYPE_DESCRIPTIONS: Record<ClientType, string> = {
    [ClientType.Pharma]:
        "Drug manufacturers, patient services, HUB ops, and specialty pharmacy programs.",
    [ClientType.Payer]:
        "Health plans and insurers — member services, benefits, and claims support.",
    [ClientType.Provider]:
        "Health systems and clinics — patient access, scheduling, and care coordination.",
    [ClientType.Other]: "General customer experience context.",
};

type Props = {
    store: ClientBrainStore;
    canEdit?: boolean;
};

const ClientTypeSection: React.FC<Props> = observer(
    ({ store, canEdit = false }) => {
        const theme = useTheme();
        const clientType = store.organizationContext.clientType;
        const selectedOption = CLIENT_TYPE_OPTIONS.find(
            (o) => o.value === clientType,
        );

        return (
            <ClientBrainSectionCard
                title="Client Type"
                subtitle="Tells KBo which industry context to reference when generating and framing research findings."
            >
                {store.isLoading ? (
                    <Skeleton variant="rounded" width={220} height={40} />
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                            flexWrap: "wrap",
                        }}
                    >
                        <Box sx={{ minWidth: "220px" }}>
                            <AcxSelectSingle
                                id="client-brain-client-type"
                                options={CLIENT_TYPE_OPTIONS}
                                valueField="value"
                                labelField="label"
                                defaultValue={selectedOption}
                                placeholder="Select client type"
                                isDisabled={!canEdit}
                                onChange={(option: ClientTypeOption) =>
                                    store.setClientType(option.value)
                                }
                                fullWidth
                            />
                        </Box>
                        {clientType && (
                            <Typography
                                sx={{
                                    fontSize: "13px",
                                    color: theme.palette.gray[500],
                                    flex: 1,
                                    minWidth: "240px",
                                }}
                            >
                                {CLIENT_TYPE_DESCRIPTIONS[clientType]}
                            </Typography>
                        )}
                    </Box>
                )}
            </ClientBrainSectionCard>
        );
    },
);

export default ClientTypeSection;
